"use client"

import { motion } from "framer-motion"
import { FaMapMarkerAlt, FaIdCard, FaGlobe, FaBuilding, FaInfoCircle, FaTimes, FaSpinner } from "react-icons/fa"
import Swal from "sweetalert2"

const inputVariants = {
  focus: { scale: 1.02, transition: { duration: 0.2 } },
}

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
}

const modalVariants = {
  hidden: { opacity: 0, scale: 0.8, y: 50 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    y: 50,
    transition: {
      duration: 0.2,
    },
  },
}

const AdditionalLawFirmModal = ({ formData, handleChange, handleSubmit, closeModal, isSubmitting }) => {
  const onClose = () => {
    // Confirmar antes de cerrar el modal si hay datos ingresados
    if (formData.website || formData.about_us || formData.address || formData.city) {
      Swal.fire({
        title: "¿Estás seguro?",
        text: "Si cierras esta ventana perderás los datos ingresados",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "var(--navy-blue)",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, cerrar",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (result.isConfirmed) {
          closeModal()
        }
      })
    } else {
      closeModal()
    }
  }

  const validateAndSubmit = (e) => {
    e.preventDefault()

    // Validar campos requeridos
    if (!formData.address || !formData.city || !formData.province) {
      Swal.fire({
        title: "Campos incompletos",
        text: "Por favor completa todos los campos obligatorios",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: "var(--navy-blue)",
      })
      return
    }

    // Si todo está bien, enviar el formulario
    handleSubmit(e)
  }

  // Función para manejar cambios en los campos de ubicación
  const handleUbicationChange = (e) => {
    const { name, value } = e.target
    handleChange({
      target: {
        name: `org.ubication.${name}`,
        value,
      },
    })
  }

  // Función para manejar cambios en los campos normales del bufete
  const handleOrgChange = (e) => {
    const { name, value } = e.target
    handleChange({
      target: {
        name: `org.${name}`,
        value,
      },
    })
  }

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-xl shadow-2xl overflow-hidden w-full max-w-2xl"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-[var(--navy-blue)] to-[var(--navy-blue-light)] py-4 px-6 flex justify-between items-center">
          <div>
            <h3 className="text-white text-xl font-semibold">Información Adicional del Bufete</h3>
            <p className="text-blue-100 text-sm">Paso 3 de 3</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
            disabled={isSubmitting}
          >
            <FaTimes size={20} />
          </button>
        </div>

        <form onSubmit={validateAndSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <motion.div variants={inputVariants} whileFocus="focus">
              <label htmlFor="address" className="block mb-1 text-sm font-medium text-gray-700">
                Dirección
              </label>
              <div className="relative">
                <FaMapMarkerAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleUbicationChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                  required
                  placeholder="Calle, número, piso"
                  disabled={isSubmitting}
                />
              </div>
            </motion.div>

            <motion.div variants={inputVariants} whileFocus="focus">
              <label htmlFor="postal_code" className="block mb-1 text-sm font-medium text-gray-700">
                Código Postal
              </label>
              <div className="relative">
                <FaMapMarkerAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  id="postal_code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleUbicationChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                  required
                  placeholder="Código postal"
                  disabled={isSubmitting}
                />
              </div>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <motion.div variants={inputVariants} whileFocus="focus">
              <label htmlFor="city" className="block mb-1 text-sm font-medium text-gray-700">
                Ciudad
              </label>
              <div className="relative">
                <FaBuilding className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleUbicationChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                  required
                  placeholder="Ciudad"
                  disabled={isSubmitting}
                />
              </div>
            </motion.div>

            <motion.div variants={inputVariants} whileFocus="focus">
              <label htmlFor="province" className="block mb-1 text-sm font-medium text-gray-700">
                Provincia
              </label>
              <div className="relative">
                <FaBuilding className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  id="province"
                  name="province"
                  value={formData.province}
                  onChange={handleUbicationChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                  required
                  placeholder="Provincia"
                  disabled={isSubmitting}
                />
              </div>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <motion.div variants={inputVariants} whileFocus="focus">
              <label htmlFor="numColegiado" className="block mb-1 text-sm font-medium text-gray-700">
                Número de Colegiado
              </label>
              <div className="relative">
                <FaIdCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  id="numColegiado"
                  name="numColegiado"
                  value={formData.numColegiado}
                  onChange={handleOrgChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                  placeholder="Número de colegiado (opcional)"
                  disabled={isSubmitting}
                />
              </div>
            </motion.div>

            <motion.div variants={inputVariants} whileFocus="focus">
              <label htmlFor="website" className="block mb-1 text-sm font-medium text-gray-700">
                Sitio Web
              </label>
              <div className="relative">
                <FaGlobe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleOrgChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                  placeholder="https://www.ejemplo.com (opcional)"
                  disabled={isSubmitting}
                />
              </div>
            </motion.div>
          </div>

          <motion.div variants={inputVariants} whileFocus="focus">
            <label htmlFor="about_us" className="block mb-1 text-sm font-medium text-gray-700">
              Descripción del Bufete
            </label>
            <div className="relative">
              <FaInfoCircle className="absolute left-3 top-3 text-gray-400" />
              <textarea
                id="about_us"
                name="about_us"
                value={formData.about_us}
                onChange={handleOrgChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
                rows={3}
                placeholder="Breve descripción de su bufete y servicios ofrecidos"
                disabled={isSubmitting}
              ></textarea>
            </div>
          </motion.div>

          <div className="flex gap-4 pt-4">
            <motion.button
              type="button"
              onClick={onClose}
              className="w-1/2 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 transition duration-300 font-medium"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={isSubmitting}
            >
              Volver
            </motion.button>
            <motion.button
              type="submit"
              className="w-1/2 bg-gradient-to-r from-[var(--navy-blue)] to-[var(--navy-blue-light)] text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-indigo-800 transition duration-300 font-medium flex items-center justify-center"
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  Procesando...
                </>
              ) : (
                "Registrar Bufete"
              )}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  )
}

export default AdditionalLawFirmModal

